import {
  Bell,
  BookOpen,
  ChartColumn,
  ClipboardList,
  Ellipsis,
  Home,
  Inbox,
  LogIn,
  LogOut,
  Monitor,
  Moon,
  Plus,
  Search,
  Settings,
  Sun,
  type LucideIcon,
} from 'lucide-react';
import { Suspense, useState, type ReactNode } from 'react';
import { Link, NavLink, Navigate, Outlet, useLocation } from 'react-router';
import { useLiveUpdates } from '../lib/live.ts';
import { useUnreadCount } from '../lib/notifications.ts';
import { signOut, useSession, useSetSessionUser } from '../lib/session.ts';
import { useTheme, type ThemeChoice } from '../lib/theme.ts';
import { AnnouncementBanner } from './Announcements.tsx';
import { AppStatus } from './AppStatus.tsx';
import { InstallBanner } from './InstallBanner.tsx';
import { ItSidebar } from './ItNav.tsx';
import { NotificationBell, UnreadDot } from './NotificationBell.tsx';
import { StatusDialog, statusText } from './StatusDialog.tsx';
import { ButtonLink } from './ui/button.tsx';
import { Avatar, Spinner } from './ui/misc.tsx';
import { Popover } from './ui/popover.tsx';
import { NotFoundPage } from '../pages/NotFoundPage.tsx';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

/** Before signing in: everything a visitor can do without an account. */
const PUBLIC_NAV: NavItem[] = [
  { to: '/new', label: 'Report a problem', icon: Plus },
  { to: '/status', label: 'Check status', icon: Search },
  { to: '/help', label: 'Help articles', icon: BookOpen },
];

const STAFF_NAV: NavItem[] = [
  { to: '/tickets', label: 'My tickets', icon: Inbox },
  { to: '/help', label: 'Help articles', icon: BookOpen },
];

// IT's pages live in the sidebar and the phone's "More" page (ItNav.tsx).

function BrandMark() {
  return (
    <Link to="/" className="flex items-center gap-2.5 rounded-lg">
      <img src="/apple-touch-icon.png" alt="" width={36} height={36} className="size-9 rounded-lg bg-white" />
      <span className="leading-tight">
        <span className="block text-[15px] font-bold tracking-tight">IT Helpdesk</span>
        <span className="block text-xs text-muted">NABOCADO</span>
      </span>
    </Link>
  );
}

const THEMES: { value: ThemeChoice; label: string; icon: LucideIcon }[] = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'Device', icon: Monitor },
];

function ThemeSwitch() {
  const [theme, setTheme] = useTheme();
  return (
    <div role="radiogroup" aria-label="Theme" className="flex rounded-lg bg-surface-2 p-0.5">
      {THEMES.map(({ value, label, icon: Icon }) => (
        <button
          key={value}
          type="button"
          role="radio"
          aria-checked={theme === value}
          onClick={() => setTheme(value)}
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium ${
            theme === value ? 'bg-surface text-text shadow-card' : 'text-muted hover:text-text'
          }`}
        >
          <Icon className="size-3.5" aria-hidden />
          {label}
        </button>
      ))}
    </div>
  );
}

/** Signed-out visitors have no account menu: a one-tap light/dark switch instead. */
function ThemeToggle() {
  const [, setTheme, dark] = useTheme();
  const label = dark ? 'Switch to light mode' : 'Switch to dark mode';
  const Icon = dark ? Sun : Moon;
  return (
    <button
      type="button"
      onClick={() => setTheme(dark ? 'light' : 'dark')}
      aria-label={label}
      title={label}
      className="flex size-9 items-center justify-center rounded-lg text-muted hover:bg-surface-2 hover:text-text"
    >
      <Icon className="size-[18px]" aria-hidden />
    </button>
  );
}

function UserMenu() {
  const { user, isIt } = useSession();
  const setUser = useSetSessionUser();
  // Outside the popover, which closes when the dialog takes the focus.
  const [statusOpen, setStatusOpen] = useState(false);
  if (!user) return null;
  const name = user.name || user.email;
  const status = statusText(user);
  return (
    <>
      <Popover label="Account menu" trigger={<Avatar name={name} />} className="w-72 p-2">
        {(close) => (
          <div>
            <div className="px-2 py-2">
              <p className="truncate font-semibold">{name}</p>
              <p className="truncate text-sm text-muted">{user.email}</p>
            </div>
            {isIt && (
              <button
                type="button"
                onClick={() => {
                  close();
                  setStatusOpen(true);
                }}
                className="mx-2 mb-1 flex w-[calc(100%-1rem)] items-center gap-2 rounded-lg border border-line px-2.5 py-2 text-left text-sm hover:bg-surface-2"
              >
                <span
                  className={`size-2.5 shrink-0 rounded-full ${status.away ? 'bg-warning' : 'bg-brand'}`}
                  aria-hidden
                />
                <span className="min-w-0 flex-1 truncate">{status.text}</span>
                <span className="text-xs font-medium text-brand">Change</span>
              </button>
            )}
            <div className="border-b border-line px-2 pt-2 pb-3">
              <ThemeSwitch />
            </div>
            <Link
              to="/settings"
              onClick={close}
              className="flex items-center gap-2.5 rounded-lg px-2 py-2 text-sm hover:bg-surface-2"
            >
              <Settings className="size-4 text-muted" aria-hidden />
              Profile &amp; notifications
            </Link>
            <button
              type="button"
              onClick={() => void signOut(setUser)}
              className="flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-sm hover:bg-surface-2"
            >
              <LogOut className="size-4 text-muted" aria-hidden />
              Sign out
            </button>
          </div>
        )}
      </Popover>
      {statusOpen && <StatusDialog onClose={() => setStatusOpen(false)} />}
    </>
  );
}

const TAB = 'flex flex-1 flex-col items-center gap-0.5 py-2 text-[11px] font-medium';
const tabTone = ({ isActive }: { isActive: boolean }) => `${TAB} ${isActive ? 'text-brand' : 'text-muted'}`;

function ReportTab() {
  return (
    <NavLink to="/new" className={TAB}>
      <span className="-mt-5 flex size-11 items-center justify-center rounded-full bg-brand text-on-brand shadow-lg">
        <Plus className="size-6" aria-hidden />
      </span>
      <span className="text-muted">Report</span>
    </NavLink>
  );
}

const BAR =
  'pb-safe fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface/95 backdrop-blur md:hidden';

/** Phones, before signing in: the same bar shape staff get afterwards. */
function PublicBottomNav() {
  return (
    <nav aria-label="Main" className={BAR}>
      <div className="mx-auto flex max-w-md items-stretch">
        <NavLink to="/" end className={tabTone}>
          <Home className="size-5" aria-hidden />
          Home
        </NavLink>
        <NavLink to="/status" className={tabTone}>
          <Search className="size-5" aria-hidden />
          Status
        </NavLink>
        <ReportTab />
        <NavLink to="/help" className={tabTone}>
          <BookOpen className="size-5" aria-hidden />
          Help
        </NavLink>
        <NavLink to="/sign-in" className={tabTone}>
          <LogIn className="size-5" aria-hidden />
          Sign in
        </NavLink>
      </div>
    </nav>
  );
}

function BottomNav() {
  const { isIt } = useSession();
  const unread = useUnreadCount(true).data ?? 0;
  const tone = tabTone;
  return (
    <nav aria-label="Main" className={BAR}>
      <div className="mx-auto flex max-w-md items-stretch">
        {isIt ? (
          <>
            <NavLink to="/queue" className={tone}>
              <ClipboardList className="size-5" aria-hidden />
              Queue
            </NavLink>
            <NavLink to="/dashboard" className={tone}>
              <ChartColumn className="size-5" aria-hidden />
              Dashboard
            </NavLink>
          </>
        ) : (
          <NavLink to="/tickets" className={tone}>
            <Inbox className="size-5" aria-hidden />
            Tickets
          </NavLink>
        )}
        <ReportTab />
        {!isIt && (
          <NavLink to="/help" className={tone}>
            <BookOpen className="size-5" aria-hidden />
            Help
          </NavLink>
        )}
        <NavLink to="/notifications" className={tone}>
          <span className="relative">
            <Bell className="size-5" aria-hidden />
            <UnreadDot count={unread} />
          </span>
          Alerts
        </NavLink>
        {isIt && (
          <NavLink to="/more" className={tone}>
            <Ellipsis className="size-5" aria-hidden />
            More
          </NavLink>
        )}
      </div>
    </nav>
  );
}

export function AppShell() {
  const { user, isIt } = useSession();
  useLiveUpdates(user?.id ?? null);
  const { pathname } = useLocation();
  const nav = user ? STAFF_NAV : PUBLIC_NAV;

  return (
    <div className="flex min-h-dvh flex-col">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded-lg focus:bg-surface focus:px-3 focus:py-2"
      >
        Skip to content
      </a>
      <AppStatus />
      <header className="sticky top-0 z-30 border-b border-line bg-surface/90 backdrop-blur">
        <div className={`flex h-16 items-center gap-4 px-4 ${isIt ? '' : 'mx-auto max-w-5xl'}`}>
          <BrandMark />
          {/* IT use the sidebar instead. */}
          <nav aria-label="Main" className={`ml-4 hidden items-center gap-1 ${isIt ? '' : 'md:flex'}`}>
            {nav.map(({ to, label }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  `rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-brand-soft text-brand-ink'
                      : 'text-muted hover:bg-surface-2 hover:text-text'
                  }`
                }
              >
                {label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-1">
            {user ? (
              <>
                {/* Hidden on phones, where the bottom bar has a Report button. */}
                <div className="mr-2 hidden md:block">
                  <ButtonLink to="/new" size="sm">
                    <Plus className="size-4" aria-hidden />
                    Report a problem
                  </ButtonLink>
                </div>
                <div className="hidden md:block">
                  <NotificationBell />
                </div>
                <UserMenu />
              </>
            ) : (
              <>
                <ThemeToggle />
                {/* Phones have Sign in in the bottom bar. */}
                <div className="hidden md:block">
                  <ButtonLink to="/sign-in" variant="ghost" size="sm">
                    Sign in
                  </ButtonLink>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        {isIt && <ItSidebar />}
        <main id="main" className="min-w-0 flex-1">
          <div
            className={`mx-auto w-full px-4 py-6 sm:py-8 ${isIt ? 'max-w-[88rem] md:px-8' : 'max-w-5xl'} ${
              user ? 'pb-28 md:pb-10' : 'pb-10'
            }`}
          >
            <InstallBanner />
            {/* The report form shows known outages itself, more prominently. */}
            {pathname !== '/new' && (
              <div className="mb-6 empty:hidden">
                <AnnouncementBanner />
              </div>
            )}
            <Suspense fallback={<Spinner />}>
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>

      {!user && (
        <footer className="border-t border-line px-4 pt-6 pb-28 text-center text-sm text-muted md:pb-6">
          NABOCADO IT Helpdesk · For urgent problems, call the IT office directly.
        </footer>
      )}
      {user ? <BottomNav /> : <PublicBottomNav />}
    </div>
  );
}

/**
 * Pages that need a signed-in user (optionally IT or admin). Others are sent
 * to sign in and brought back after; the wrong role sees "not found".
 */
export function RequireAuth({ children, role }: { children: ReactNode; role?: 'it' | 'admin' }) {
  const { user, isLoading, isIt, isAdmin } = useSession();
  const location = useLocation();
  if (isLoading) return <Spinner />;
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/sign-in?next=${next}`} replace />;
  }
  if ((role === 'it' && !isIt) || (role === 'admin' && !isAdmin)) return <NotFoundPage />;
  return <>{children}</>;
}
